/**
 * Utilitário para carregar o Facebook SDK dinamicamente
 * 
 * Usado para o fluxo de Embedded Signup do WhatsApp Business.
 * O SDK é carregado sob demanda (lazy load) apenas quando necessário.
 */

import { i18n } from "../translate/i18n";

let sdkLoaded = false;
let sdkLoading = false;
let loadCallbacks = [];

/**
 * Carrega o Facebook SDK de forma assíncrona
 * @param {string} appId - O Facebook App ID
 * @returns {Promise<void>}
 */
export const loadFacebookSDK = (appId) => {
  return new Promise((resolve, reject) => {
    // Se já carregou, resolve imediatamente
    if (sdkLoaded && window.FB) {
      resolve(window.FB);
      return;
    }

    // Se está carregando, adiciona callback
    if (sdkLoading) {
      loadCallbacks.push({ resolve, reject });
      return;
    }

    sdkLoading = true;
    loadCallbacks.push({ resolve, reject });

    // Configurar callback global
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: "v25.0",
      });

      sdkLoaded = true;
      sdkLoading = false;

      // Resolver todas as promessas pendentes
      loadCallbacks.forEach((cb) => cb.resolve(window.FB));
      loadCallbacks = [];
    };

    // Injetar script do SDK
    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.src = "https://connect.facebook.net/pt_BR/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";

    script.onerror = () => {
      sdkLoading = false;
      const error = new Error(i18n.t("facebookSdk.errors.loadFailed"));
      loadCallbacks.forEach((cb) => cb.reject(error));
      loadCallbacks = [];
    };

    // Verificar se já existe um script do SDK
    const existingScript = document.getElementById("facebook-jssdk");
    if (existingScript) {
      existingScript.remove();
    }

    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });
};

/**
 * Inicia o fluxo de Embedded Signup do WhatsApp
 * 
 * @param {Object} options
 * @param {string} options.appId - Facebook App ID
 * @param {string} options.configId - Config ID do Embedded Signup
 * @param {function} options.onSuccess - Callback com o código de autorização
 * @param {function} options.onError - Callback de erro
 */
export const launchWhatsAppSignup = async ({ appId, configId, onSuccess, onError }) => {
  try {
    await loadFacebookSDK(appId);

    // Registrar handler de mensagens para capturar o session-info-event
    // que contém o phone_number_id e waba_id diretamente
    const messageHandler = (event) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          // data.data contém { phone_number_id, waba_id }
          if (data.data) {
            // Dispara evento customizado para o modal capturar
            window.dispatchEvent(
              new CustomEvent("wa-embedded-signup-info", { detail: data.data })
            );
          }
        }
      } catch (e) {
        // Ignorar mensagens que não são JSON
      }
    };

    window.addEventListener("message", messageHandler);

    // Iniciar login com Facebook
    window.FB.login(
      (response) => {
        window.removeEventListener("message", messageHandler);

        if (response.authResponse) {
          const code = response.authResponse.code;
          if (code) {
            onSuccess(code);
          } else {
            // Fallback: se não tem code, pode ter accessToken (less ideal)
            onError(i18n.t("facebookSdk.errors.noAuthCode"));
          }
        } else {
          onError(i18n.t("facebookSdk.errors.loginCancelled"));
        }
      },
      {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          feature: "whatsapp_embedded_signup",
          sessionInfoVersion: 2,
        },
      }
    );
  } catch (err) {
    onError(err.message || i18n.t("facebookSdk.errors.startFailed"));
  }
};

export default { loadFacebookSDK, launchWhatsAppSignup };
